/**
 * Condition label templates for i18n.
 * Each key maps to a human-readable pattern with placeholders: {deviceName}, {value}, {channel}.
 * English defaults use “When …” phrasing for consistency with the flow canvas.
 * Use with $format('key', { values: { deviceName, value, channel } })
 */

export type ConditionTemplateKey =
	| 'condition_if_device_on'
	| 'condition_if_device_off'
	| 'condition_if_device_consumption_more_than'
	| 'condition_if_device_consumption_less_than'
	| 'condition_if_device_temperature_above'
	| 'condition_if_device_temperature_below'
	| 'condition_if_device_humidity_above'
	| 'condition_if_device_humidity_below'
	| 'condition_if_device_brightness_above'
	| 'condition_if_device_brightness_below'
	| 'condition_if_device_battery_above'
	| 'condition_if_device_battery_below'
	| 'condition_if_device_door_open'
	| 'condition_if_device_door_close'
	| 'condition_if_device_window_open'
	| 'condition_if_device_window_close'
	| 'condition_if_device_motion_detected'
	| 'condition_if_device_motion_not_detected'
	| 'condition_if_device_roller_position_above'
	| 'condition_if_device_roller_position_below'
	| 'condition_if_device_roller_state'
	| 'condition_if_device_push'
	| 'condition_if_device_generic';

export interface ConditionTemplateResult {
	key: ConditionTemplateKey;
	values: { deviceName: string; value?: string; channel?: string };
	default: string;
}

/** Natural phrase for generic fallback labels (never leak `less_than` snake_case). */
function operatorPhraseForGenericFallback(operator: string): string {
	const op = String(operator || '')
		.toLowerCase()
		.trim()
		.replace(/\s+/g, '_');
	switch (op) {
		case 'eq':
		case 'equal':
			return 'is';
		case 'lt':
		case 'less_than':
			return 'is less than';
		case 'gt':
		case 'more_than':
			return 'is more than';
		case 'le':
			return 'is at most';
		case 'ge':
			return 'is at least';
		case 'ne':
		case 'neq':
		case 'not_equal':
			return 'is not';
		default:
			return op.replace(/_/g, ' ');
	}
}

/**
 * Resolve condition to i18n template key and values.
 * Covers device conditions from init_device_conditions.
 */
export function getConditionTemplate(
	deviceName: string,
	propertyName: string,
	operator: string,
	value: any,
	channel?: number | string,
): ConditionTemplateResult {
	const channelSuffix =
		channel !== undefined && channel !== 0 && channel !== '0'
			? ` (${typeof channel === 'number' ? `Channel ${channel}` : channel})`
			: '';
	const deviceLabel = `${deviceName}${channelSuffix}`;
	const val = String(value ?? '').toLowerCase();
	const op = String(operator || '')
		.toLowerCase()
		.trim()
		.replace(/\s+/g, '_');
	const propPretty = propertyName.replace(/_/g, ' ');

	// Turn / state (on/off)
	if (
		['turn', 'state'].includes(propertyName.toLowerCase()) ||
		propertyName.toLowerCase().startsWith('turn')
	) {
		if (val === 'on' || val === '1' || val === 'true') {
			return {
				key: 'condition_if_device_on',
				values: { deviceName: deviceLabel },
				default: `When ${deviceLabel} ${propPretty} is On`,
			};
		}
		if (val === 'off' || val === '0' || val === 'false') {
			return {
				key: 'condition_if_device_off',
				values: { deviceName: deviceLabel },
				default: `When ${deviceLabel} ${propPretty} is Off`,
			};
		}
	}

	// Consumption / power / meter (internal ids: meter, vol_meter, emeter, *_power, …)
	if (
		['meter', 'power meter', 'consumption', 'vol_meter', 'emeter', 'power', 'watt'].some((p) =>
			propertyName.toLowerCase().includes(p),
		)
	) {
		const v = String(value ?? '');
		if (op === 'gt' || op === 'more_than') {
			return {
				key: 'condition_if_device_consumption_more_than',
				values: { deviceName: deviceLabel, value: v },
				default: `When ${deviceLabel} consumption is more than ${v}`,
			};
		}
		if (op === 'lt' || op === 'less_than') {
			return {
				key: 'condition_if_device_consumption_less_than',
				values: { deviceName: deviceLabel, value: v },
				default: `When ${deviceLabel} consumption is less than ${v}`,
			};
		}
	}

	// Temperature
	if (propertyName.toLowerCase().includes('temperature') || propertyName === 'tmp') {
		const v = String(value ?? '');
		if (op === 'gt' || op === 'more_than') {
			return {
				key: 'condition_if_device_temperature_above',
				values: { deviceName: deviceLabel, value: v },
				default: `When ${deviceLabel} temperature is above ${v}°C`,
			};
		}
		if (op === 'lt' || op === 'less_than') {
			return {
				key: 'condition_if_device_temperature_below',
				values: { deviceName: deviceLabel, value: v },
				default: `When ${deviceLabel} temperature is below ${v}°C`,
			};
		}
	}

	// Humidity
	if (propertyName.toLowerCase().includes('humidity')) {
		const v = String(value ?? '');
		if (op === 'gt' || op === 'more_than') {
			return {
				key: 'condition_if_device_humidity_above',
				values: { deviceName: deviceLabel, value: v },
				default: `When ${deviceLabel} humidity is above ${v}%`,
			};
		}
		if (op === 'lt' || op === 'less_than') {
			return {
				key: 'condition_if_device_humidity_below',
				values: { deviceName: deviceLabel, value: v },
				default: `When ${deviceLabel} humidity is below ${v}%`,
			};
		}
	}

	// Brightness / illumination
	if (['brightness', 'illumination', 'lux'].some((p) => propertyName.toLowerCase().includes(p))) {
		const v = String(value ?? '');
		if (op === 'gt' || op === 'more_than') {
			return {
				key: 'condition_if_device_brightness_above',
				values: { deviceName: deviceLabel, value: v },
				default: `When ${deviceLabel} brightness is above ${v}`,
			};
		}
		if (op === 'lt' || op === 'less_than') {
			return {
				key: 'condition_if_device_brightness_below',
				values: { deviceName: deviceLabel, value: v },
				default: `When ${deviceLabel} brightness is below ${v}`,
			};
		}
	}

	// Battery
	if (propertyName.toLowerCase().includes('battery')) {
		const v = String(value ?? '');
		if (op === 'gt' || op === 'more_than') {
			return {
				key: 'condition_if_device_battery_above',
				values: { deviceName: deviceLabel, value: v },
				default: `When ${deviceLabel} battery is above ${v}%`,
			};
		}
		if (op === 'lt' || op === 'less_than') {
			return {
				key: 'condition_if_device_battery_below',
				values: { deviceName: deviceLabel, value: v },
				default: `When ${deviceLabel} battery is below ${v}%`,
			};
		}
	}

	// Door / window
	if (propertyName.toLowerCase() === 'door') {
		if (val === 'open') {
			return {
				key: 'condition_if_device_door_open',
				values: { deviceName: deviceLabel },
				default: `When ${deviceLabel} door is open`,
			};
		}
		if (val === 'close' || val === 'closed') {
			return {
				key: 'condition_if_device_door_close',
				values: { deviceName: deviceLabel },
				default: `When ${deviceLabel} door is closed`,
			};
		}
	}
	if (propertyName.toLowerCase() === 'window') {
		if (val === 'open') {
			return {
				key: 'condition_if_device_window_open',
				values: { deviceName: deviceLabel },
				default: `When ${deviceLabel} window is open`,
			};
		}
		if (val === 'close' || val === 'closed') {
			return {
				key: 'condition_if_device_window_close',
				values: { deviceName: deviceLabel },
				default: `When ${deviceLabel} window is closed`,
			};
		}
	}

	// Motion / vibration / smoke / flood / gas
	if (
		['motion', 'vibration', 'pir', 'smoke', 'flood', 'gas', 'moisture_alarm'].some((p) =>
			propertyName.toLowerCase().includes(p),
		)
	) {
		if (val === 'detected' || val === 'on') {
			return {
				key: 'condition_if_device_motion_detected',
				values: { deviceName: deviceLabel },
				default: `When ${deviceLabel} is detected`,
			};
		}
		if (val === 'not detected' || val === 'off') {
			return {
				key: 'condition_if_device_motion_not_detected',
				values: { deviceName: deviceLabel },
				default: `When ${deviceLabel} is not detected`,
			};
		}
	}

	// Roller position
	if (
		['roller position', 'multi_cover_position'].some((p) => propertyName.toLowerCase().includes(p))
	) {
		const v = String(value ?? '');
		if (op === 'gt' || op === 'more_than') {
			return {
				key: 'condition_if_device_roller_position_above',
				values: { deviceName: deviceLabel, value: v },
				default: `When ${deviceLabel} position is above ${v}%`,
			};
		}
		if (op === 'lt' || op === 'less_than') {
			return {
				key: 'condition_if_device_roller_position_below',
				values: { deviceName: deviceLabel, value: v },
				default: `When ${deviceLabel} position is below ${v}%`,
			};
		}
	}

	// Roller state (open/close/stop)
	if (['roller state', 'multi_cover_state'].some((p) => propertyName.toLowerCase().includes(p))) {
		const v = String(value ?? '').replace(/_/g, ' ');
		return {
			key: 'condition_if_device_roller_state',
			values: { deviceName: deviceLabel, value: v },
			default: `When ${deviceLabel} is ${v}`,
		};
	}

	// Push type
	if (['push_type', 'input_value'].some((p) => propertyName.toLowerCase().includes(p))) {
		const v = String(value ?? '').replace(/_/g, ' ');
		return {
			key: 'condition_if_device_push',
			values: { deviceName: deviceLabel, value: v },
			default: `When ${deviceLabel} ${v}`,
		};
	}

	// Generic fallback
	const formattedVal = String(value ?? '');
	const opPhrase = operatorPhraseForGenericFallback(operator);
	return {
		key: 'condition_if_device_generic',
		values: { deviceName: deviceLabel, value: formattedVal },
		default: `When ${deviceLabel} ${propPretty} ${opPhrase} ${formattedVal}`.trim(),
	};
}
